import { FC, useEffect, useState } from "react";
import { Button, Dialog, Drawer, Tab, Tabs, Typography } from "@mui/material";
import { useTypedSelector } from "../../../hooks/useTypedSelector";
import {
  AddressBody,
  AddressType,
  useCreateAddressMutation,
  useDeleteAddressMutation,
  useSetDefaultAddressMutation,
} from "../../../store";
import { AddressCard, AddressDialog } from "../../organisms";

interface Props {
  title: string;
  address: AddressType[];
  selectedAddressId?: number;
  selectAddress?: (address: AddressType) => void;
}

export const AddressMain: FC<Props> = ({
  address,
  title,
  selectedAddressId,
  selectAddress,
}) => {
  const { userId } = useTypedSelector(state => state.auth);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("india");

  const [setDefaultAddress] = useSetDefaultAddressMutation();
  const [deleteAddress] = useDeleteAddressMutation();
  const [
    createAddress,
    { isSuccess: createAddressSuccess, reset: createAddressReset },
  ] = useCreateAddressMutation();

  useEffect(() => {
    if (createAddressSuccess) {
      setShowModal(false);
      createAddressReset();
    }
  }, [createAddressSuccess]);

  const onSubmitAddressModal = async (
    address: Omit<AddressBody, "customerId">
  ) => {
    await createAddress({ ...address, customerId: userId! });
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="space-y-6">
          <div className="bg-stone-50 rounded-md">
            <Tabs
              className="flex-1"
              value={activeTab}
              onChange={(e, value) => setActiveTab(value)}>
              <Tab label="India" value="india" />
              <Tab label="Rest of the world" value="rest-of-the-world" />
            </Tabs>
          </div>
          <div className="flex flex-col gap-4 lg:gap-8">
            <div className="flex justify-between items-center">
              <Typography variant="subtitle1" fontWeight={700}>
                {title}
              </Typography>
              <Button size="small" onClick={() => setShowModal(true)}>
                Add Address
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-6 lg:gap-12">
              {address
                .filter(a => {
                  if (activeTab === "india")
                    return a.country.toLowerCase() === "india";
                  return a.country.toLocaleLowerCase() !== "india";
                })
                .map(address => (
                  <div key={address.id} className="col-span-6">
                    <AddressCard
                      address={address}
                      select={selectAddress}
                      isSelected={selectedAddressId === address.id}
                      isDefault={address.isDefaultAddress}
                      actionButtons={[
                        {
                          label: "Make Default",
                          onClick: () => setDefaultAddress(address.id),
                        },
                        {
                          label: "Delete",
                          onClick: () => deleteAddress(address.id),
                          color: "error",
                        },
                      ]}
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <>
          <Dialog
            disablePortal
            fullWidth
            open={showModal}
            onClose={() => setShowModal(false)}
            sx={{ display: { xs: "none", lg: "block" } }}>
            <AddressDialog
              open={showModal}
              onClose={() => setShowModal(false)}
              onSubmit={onSubmitAddressModal}
              currency={activeTab === "india" ? "INR" : "USD"}
            />
          </Dialog>
          <Drawer
            disablePortal
            open={showModal}
            anchor="bottom"
            sx={{ display: { xs: "block", lg: "none" } }}>
            <AddressDialog
              open={showModal}
              onClose={() => setShowModal(false)}
              onSubmit={onSubmitAddressModal}
              currency={activeTab === "india" ? "INR" : "USD"}
            />
          </Drawer>
        </>
      )}
    </>
  );
};
